import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { vcardMicrositeAPIService } from '~/api/microsites/vcard-plus';
import VCardContactInfo from '~/components/VCardPlusQRContent/VCardPlusBasicInformation/VCardContactInfo';
import VCardPlusAboutYou from '~/components/VCardPlusQRContent/VCardPlusBasicInformation/VCardPlusAboutYou/VCardPlusAboutYou';
import VCardPlusLocation from '~/components/VCardPlusQRContent/VCardPlusBasicInformation/VCardPlusLocation';
import VCardPlusSummary from '~/components/VCardPlusQRContent/VCardPlusBasicInformation/VCardPlusSummary';
import VCardPlusContent from '~/components/VCardPlusQRContent/VCardPlusContent/VCardPlusContent';
import { DESKTOP_MIN_WIDTH, MAX_CONTENT_WIDTH } from '~/constants/common';
import { qrCodeKeys } from '~/constants/queryKeys';
import {
  DEFAULT_VCARD_COMPANY,
  DEFAULT_VCARD_EMAIL,
  DEFAULT_VCARD_PHONE,
  DEFAULT_VCARD_WEBSITE,
} from '~/constants/vCard';
import { useQrCodeById, useVCardQrById } from '~/queries/qrCode';
import { getWindowWidth } from '~/store/reducers/app/selectors';

type AnonymousVCardPlusQRProps = {
  qrCodeId: string;
  vCardId: string;
};

export default function AnonymousVCardPlusQR({ qrCodeId, vCardId }: AnonymousVCardPlusQRProps) {
  const queryClient = useQueryClient();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const windowWidth = useSelector(getWindowWidth);

  const { data: vCardPlusData, isLoading: isLoadingVCardData } = useVCardQrById({ vCardId });

  const contentWidth = windowWidth > MAX_CONTENT_WIDTH ? MAX_CONTENT_WIDTH : windowWidth;
  // 380 - width of right section with phone with qr
  // 166 - 83 * 2 - padding-inline of customContainer className
  // 20 - gap between left and right sections (phone with QR is right section)
  // 60 - 30 * 2 - padding of parent container
  const gridSelectorMaxWidth = contentWidth - 166 - 32 - 20 - 60 - 380;

  const { isLoading: isLoadingQrCodeDada } = useQrCodeById({ qrCodeId });

  const { mutateAsync: updateVCardPlusData } = useMutation({
    mutationFn: (body) => vcardMicrositeAPIService.updateVCardSite({ id: vCardId, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusPersonData } = useMutation({
    mutationFn: (body) => vcardMicrositeAPIService.updateVCardPerson(body, vCardId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: createVCardPlusSocial } = useMutation({
    mutationFn: (body) => vcardMicrositeAPIService.createVCardSocial(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusSocialData } = useMutation({
    mutationFn: ({ body, socialId }) => vcardMicrositeAPIService.updateVCardSocial(body, socialId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: deleteVCardPlusSocial } = useMutation({
    mutationFn: vcardMicrositeAPIService.deleteVCardSocial,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: createVCardPlusCompany } = useMutation({
    mutationFn: vcardMicrositeAPIService.createVCardCompany,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusCompany } = useMutation({
    mutationFn: ({ body, companyId }) => vcardMicrositeAPIService.updateVCardCompany(body, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: deleteVCardPlusCompany } = useMutation({
    mutationFn: vcardMicrositeAPIService.deleteVCardCompany,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: createVCardPlusPhone } = useMutation({
    mutationFn: vcardMicrositeAPIService.createVCardPhones,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusPhone } = useMutation({
    mutationFn: ({ body, phoneId }) => vcardMicrositeAPIService.updateVCardPhones(body, phoneId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: deleteVCardPlusPhone, isPending: isPendingDeleteVCardPlusPhone } = useMutation({
    mutationFn: vcardMicrositeAPIService.deleteVCardPhones,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: createVCardPlusEmail } = useMutation({
    mutationFn: vcardMicrositeAPIService.createVCardEmails,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusEmail } = useMutation({
    mutationFn: ({ body, emailId }) => vcardMicrositeAPIService.updateVCardEmails(body, emailId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: deleteVCardPlusEmail, isPending: isPendingDeleteVCardPlusEmail } = useMutation({
    mutationFn: vcardMicrositeAPIService.deleteVCardEmails,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: createVCardPlusWebsite } = useMutation({
    mutationFn: vcardMicrositeAPIService.createVCardWebsite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusWebsite } = useMutation({
    mutationFn: ({ body, websiteId }) => vcardMicrositeAPIService.updateVCardWebsite(body, websiteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: deleteVCardPlusWebsite, isPending: isPendingDeleteVCardPlusWebsite } = useMutation({
    mutationFn: vcardMicrositeAPIService.deleteVCardWebsite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: createVCardPlusImage } = useMutation({
    mutationFn: vcardMicrositeAPIService.uploadImage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: deleteVCardPlusImage } = useMutation({
    mutationFn: vcardMicrositeAPIService.removeImage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  const { mutate: updateVCardPlusLocation } = useMutation({
    mutationFn: ({ body, locationId }) => vcardMicrositeAPIService.updateVCardLocation(body, locationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.vCardPlusQrData });
    },
  });

  useEffect(() => {
    if (vCardId) {
      createVCardPlusCompany({
        ...DEFAULT_VCARD_COMPANY,
        microsite: vCardId,
      });

      createVCardPlusPhone({
        ...DEFAULT_VCARD_PHONE,
        microsite: vCardId,
      });

      createVCardPlusEmail({
        ...DEFAULT_VCARD_EMAIL,
        microsite: vCardId,
      });

      createVCardPlusWebsite({
        ...DEFAULT_VCARD_WEBSITE,
        microsite: vCardId,
      });
    }
  }, [vCardId]);

  const isVCardInitializing = isLoadingQrCodeDada || isLoadingVCardData || !vCardPlusData;

  if (isVCardInitializing) {
    return (
      <StyledLoaderContainer>
        <CircularProgress />
      </StyledLoaderContainer>
    );
  }

  return (
    <>
      <VCardPlusAboutYou
        updateVCardPlusPersonData={updateVCardPlusPersonData}
        vCardPlusData={vCardPlusData}
        updateVCardPlusData={updateVCardPlusData}
      />
      <VCardPlusSummary defaultValue={vCardPlusData?.summary} updateVCardPlusData={updateVCardPlusData} />
      <VCardContactInfo
        updateVCardPlusData={updateVCardPlusData}
        createVCardPlusEmail={createVCardPlusEmail}
        createVCardPlusPhone={createVCardPlusPhone}
        createVCardPlusWebsite={createVCardPlusWebsite}
        vCardPlusData={vCardPlusData}
        deleteVCardPlusEmail={deleteVCardPlusEmail}
        deleteVCardPlusPhone={deleteVCardPlusPhone}
        deleteVCardPlusWebsite={deleteVCardPlusWebsite}
        updateVCardPlusEmail={updateVCardPlusEmail}
        updateVCardPlusPhone={updateVCardPlusPhone}
        updateVCardPlusWebsite={updateVCardPlusWebsite}
        isPendingDeleteVCardPlusPhone={isPendingDeleteVCardPlusPhone}
        isPendingDeleteVCardPlusEmail={isPendingDeleteVCardPlusEmail}
        isPendingDeleteVCardPlusWebsite={isPendingDeleteVCardPlusWebsite}
      />
      <VCardPlusLocation vCardPlusData={vCardPlusData} updateVCardPlusLocation={updateVCardPlusLocation} />
      <VCardPlusContent
        gridSelectorMaxWidth={isDesktop ? gridSelectorMaxWidth : undefined}
        updateVCardPlusData={updateVCardPlusData}
        createVCardPlusSocial={createVCardPlusSocial}
        updateVCardPlusSocialData={updateVCardPlusSocialData}
        vCardPlusData={vCardPlusData}
        deleteVCardPlusSocial={deleteVCardPlusSocial}
        createVCardPlusCompany={createVCardPlusCompany}
        deleteVCardPlusCompany={deleteVCardPlusCompany}
        updateVCardPlusCompany={updateVCardPlusCompany}
        createVCardPlusImage={createVCardPlusImage}
        deleteVCardPlusImage={deleteVCardPlusImage}
      />
    </>
  );
}

const StyledLoaderContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'calc(100% - 60px)',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      height: 'unset',
    },
  };
});
